import { useVirtualizer } from '@tanstack/react-virtual'
import { cx } from 'antd-style'
import {
  createElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import type { IconCatalogEntry } from './catalog'
import { useStyles } from './styles'

const CELL_MIN_WIDTH = 76
const ROW_HEIGHT = 68
const MIN_COLUMNS = 4
const OVERSCAN_ROWS = 4
const ICON_SIZE = 20

type IconPickerGridProps = {
  entries: IconCatalogEntry[]
  color: string
  selectedName: string | null
  onSelect: (name: string) => void
  onConfirm: (name: string) => void
}

export const IconPickerGrid = ({
  entries,
  color,
  selectedName,
  onSelect,
  onConfirm
}: IconPickerGridProps) => {
  const { styles } = useStyles()
  const viewportRef = useRef<HTMLDivElement>(null)
  const [columns, setColumns] = useState(MIN_COLUMNS)

  useLayoutEffect(() => {
    const viewport = viewportRef.current

    if (!viewport) return

    const measure = (width: number) => {
      if (width > 0) {
        setColumns(Math.max(MIN_COLUMNS, Math.floor(width / CELL_MIN_WIDTH)))
      }
    }

    measure(viewport.clientWidth)

    const observer = new ResizeObserver(([entry]) =>
      measure(entry.contentRect.width)
    )

    observer.observe(viewport)

    return () => observer.disconnect()
  }, [])

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: Math.ceil(entries.length / columns),
    getScrollElement: () => viewportRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN_ROWS
  })

  useEffect(() => {
    virtualizer.scrollToOffset(0)
  }, [entries, virtualizer])

  return (
    <div ref={viewportRef} className={styles.viewport}>
      <div
        className={styles.rows}
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((row) => (
          <div
            key={row.key}
            className={styles.row}
            style={{
              height: row.size,
              transform: `translateY(${row.start}px)`,
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`
            }}
          >
            {entries
              .slice(row.index * columns, row.index * columns + columns)
              .map(({ name, Icon }) => {
                const selected = name === selectedName

                return (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    aria-pressed={selected}
                    className={cx(styles.cell, selected && styles.cellSelected)}
                    onClick={() => onSelect(name)}
                    onDoubleClick={() => onConfirm(name)}
                  >
                    {createElement(Icon, { size: ICON_SIZE, color })}
                    <span
                      className={cx(
                        styles.cellLabel,
                        selected && styles.cellLabelSelected
                      )}
                    >
                      {name}
                    </span>
                  </button>
                )
              })}
          </div>
        ))}
      </div>
    </div>
  )
}
